import React, { useState } from 'react';
import { XIcon, FlagIcon, ClockIcon, SendIcon, CheckCircleIcon, FilterIcon, FileTextIcon } from 'lucide-react';
import { Condition } from '../types/budget';
interface ConditionsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  conditions: Condition[];
  onEditCondition: (condition: Condition) => void;
  onFinalizeReview?: () => void;
}
export function ConditionsPanel({
  isOpen,
  onClose,
  conditions,
  onEditCondition,
  onFinalizeReview
}: ConditionsPanelProps) {
  const [filter, setFilter] = useState<'all' | 'pending' | 'sent' | 'resolved'>('all');
  const filteredConditions = conditions.filter(c => filter === 'all' || c.status === filter);
  const getStatusIcon = (status: Condition['status']) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="w-4 h-4 text-accent-1" data-id="element-76" />;
      case 'sent':
        return <SendIcon className="w-4 h-4 text-accent" data-id="element-77" />;
      case 'resolved':
        return <CheckCircleIcon className="w-4 h-4 text-tertiary" data-id="element-78" />;
    }
  };
  const getStatusBadgeClass = (status: Condition['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-accent-1/20 text-yellow-800 border-accent-1/30';
      case 'sent':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'resolved':
        return 'bg-tertiary/10 text-tertiary border-tertiary/20';
    }
  };
  return <>
      {/* Backdrop for mobile */}
      {isOpen && <div className="fixed inset-0 bg-primary/10 backdrop-blur-sm z-30 lg:hidden" onClick={onClose} data-id="element-79" />}

      <div className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out flex flex-col border-l border-gray-100 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-80">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/50" data-id="element-81">
          <div className="flex items-center gap-2" data-id="element-82">
            <FlagIcon className="w-5 h-5 text-accent-1" data-id="element-83" />
            <h2 className="text-lg font-heading font-light text-primary" data-id="element-84">
              Modification Requests
            </h2>
            <span className="bg-gray-200 text-gray-700 text-xs py-0.5 px-2 rounded-full font-body font-normal" data-id="element-85">
              {conditions.length}
            </span>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-full transition-colors" data-id="element-86">
            <XIcon className="w-5 h-5" data-id="element-87" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100 flex items-center gap-2 overflow-x-auto" data-id="element-88">
          <FilterIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-89" />
          {(['all', 'pending', 'sent', 'resolved'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 text-xs font-body font-normal rounded-full capitalize whitespace-nowrap transition-colors ${filter === f ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-90">
              {f}
            </button>)}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-91">
          {filteredConditions.length === 0 ? <div className="text-center py-12 text-gray-400 font-body font-thin" data-id="element-92">
              <FlagIcon className="w-12 h-12 mx-auto mb-3 opacity-20" data-id="element-93" />
              <p data-id="element-94">No modification requests yet.</p>
              <p className="text-xs mt-1" data-id="element-95">
                Hover any line item and click "Add" to flag it.
              </p>
            </div> : filteredConditions.map(condition => <div key={condition.id} onClick={() => onEditCondition(condition)} className="group border border-gray-100 rounded-lg p-4 hover:border-accent/30 hover:shadow-md transition-all cursor-pointer bg-white relative overflow-hidden" data-id="element-96">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent-1" data-id="element-97" />

                <div className="flex justify-between items-start mb-2 pl-2" data-id="element-98">
                  <div data-id="element-99">
                    <p className="text-xs font-body font-thin text-gray-500 mb-0.5" data-id="element-100">
                      {condition.categoryName}
                    </p>
                    <h3 className="text-sm font-heading font-normal text-primary" data-id="element-101">
                      {condition.lineItemName}
                    </h3>
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs border ${getStatusBadgeClass(condition.status)}`} data-id="element-102">
                    {getStatusIcon(condition.status)}
                    <span className="capitalize" data-id="element-103">{condition.status}</span>
                  </div>
                </div>

                <p className="text-sm font-body font-thin text-gray-600 line-clamp-3 pl-2 mt-2 bg-gray-50 p-2 rounded" data-id="element-104">
                  {condition.note}
                </p>
              </div>)}
        </div>

        {/* Finalize Review Button */}
        {conditions.length > 0 && <div className="p-4 border-t border-gray-200 bg-gray-50/80" data-id="element-105">
            <p className="text-xs font-body font-thin text-gray-500 mb-3" data-id="element-106">
              {conditions.length} modification
              {conditions.length !== 1 ? 's' : ''} will be bundled into one
              condition for the borrower.
            </p>
            <button onClick={onFinalizeReview} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors font-body font-normal text-sm" data-id="element-107">
              <FileTextIcon className="w-4 h-4" data-id="element-108" />
              Finalize Review & Generate Condition
            </button>
          </div>}
      </div>
    </>;
}